import { useMemo, useState } from 'react';
import {
  CheckCircle2, Download, Loader2, Pencil, Plus, RotateCcw, Trash2, Wrench,
} from 'lucide-react';
import { useApi } from '@/hooks/useApi';
import { api } from '@/lib/api';
import { classNames, formatDate, toCsv } from '@/lib/utils';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
// Vive en analytics porque nació con Finance, pero el componente es genérico:
// una tarjeta con barra fija y el resto plegado detrás de «Filtros».
import PanelFiltros from '@/components/analytics/PanelFiltros';
import ModalGasto from '@/components/gastos/ModalGasto';
import ModalCobrar from '@/components/gastos/ModalCobrar';
import {
  CATEGORIAS, ETIQUETAS_ESTADO, Gasto, ListadoGastos, PropiedadGasto, formatImporte,
} from '@/components/gastos/tipos';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const ANIO_ACTUAL = new Date().getFullYear();
const ANIOS = Array.from({ length: 5 }, (_, i) => ANIO_ACTUAL - i);

interface Filtros {
  propertyId: string;
  /** '' = todos los meses. */
  mes: string;
  /** '' = todos los años. */
  anio: string;
  estado: string;
  categoria: string;
}

/** Los tres estados, en la barra fija del panel de filtros. */
const ESTADOS_BARRA = [
  { valor: 'all', etiqueta: 'Todos' },
  { valor: 'PENDIENTE', etiqueta: 'Pendientes' },
  { valor: 'COBRADO', etiqueta: 'Cobrados' },
] as const;

/** Lo que se pliega detrás del botón «Filtros»; el estado vive fuera. */
const CAMPOS_PLEGADOS = ['propertyId', 'mes', 'anio', 'categoria'] as const;

const FILTROS_INICIALES: Filtros = {
  propertyId: 'all',
  mes: '',
  // Se abre en el año en curso: el histórico entero son miles de líneas y lo que
  // se viene a hacer aquí es repasar lo de este año antes de liquidar.
  anio: String(ANIO_ACTUAL),
  estado: 'all',
  categoria: 'all',
};

/**
 * El mes y el año de la interfaz se traducen a un rango de fechas, que es lo que
 * entiende el contenedor: `expenseDate` es un `YYYY-MM-DD` suelto, sin partes.
 */
function rangoFechas(mes: string, anio: string): { startDate?: string; endDate?: string } {
  if (!anio) return {};
  const a = Number(anio);
  if (mes) {
    const m = Number(mes);
    const finDeMes = new Date(a, m, 0).getDate();
    return {
      startDate: `${a}-${String(m).padStart(2, '0')}-01`,
      endDate: `${a}-${String(m).padStart(2, '0')}-${finDeMes}`,
    };
  }
  return { startDate: `${a}-01-01`, endDate: `${a}-12-31` };
}

/**
 * Gastos de mantenimiento por propiedad.
 *
 * Los documentos viven en `sereno_lavanderia/maintenance_expenses_v2`, que es de
 * la app de lavandería; esta pantalla es una segunda ventana sobre el mismo
 * contenedor, con las mismas operaciones que allí. El CRM ya los leía al liquidar
 * —`/api/liquidaciones/lavanderia-expenses` trae los PENDIENTE del propietario y
 * al confirmar la liquidación los pasa a COBRADO—, así que un gasto que aquí
 * aparezca como cobrado puede haberlo cobrado la liquidación, no una persona.
 */
export default function GastosPage() {
  const [filtros, setFiltros] = useState<Filtros>(FILTROS_INICIALES);
  const [editando, setEditando] = useState<Gasto | null>(null);
  const [formAbierto, setFormAbierto] = useState(false);
  const [cobrando, setCobrando] = useState<Gasto | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [errorAccion, setErrorAccion] = useState<string | null>(null);

  const { data: propiedades } = useApi<PropiedadGasto[]>(() => api.getGastoPropiedades(), []);

  const { data, loading, error, refresh } = useApi<ListadoGastos>(
    () => api.getGastos({
      propertyId: filtros.propertyId,
      status: filtros.estado,
      category: filtros.categoria,
      ...rangoFechas(filtros.mes, filtros.anio),
    }),
    [filtros.propertyId, filtros.estado, filtros.categoria, filtros.mes, filtros.anio],
  );

  const gastos = data?.gastos || [];

  const totales = useMemo(() => ({
    importe: gastos.reduce((s, g) => s + g.amount, 0),
    cobrado: gastos.reduce((s, g) => s + g.chargedAmount, 0),
    pendientes: gastos.filter((g) => g.status === 'PENDIENTE').length,
  }), [gastos]);

  const cambiar = (campo: keyof Filtros, valor: string) =>
    setFiltros((f) => ({ ...f, [campo]: valor }));

  /**
   * Cuántos filtros plegados se han tocado, para el contador del botón.
   *
   * Se cuenta lo que se desvía del arranque, no lo que tiene valor: el año
   * viene puesto al actual de serie, y contarlo dejaría el botón marcado
   * siempre, que es como no marcarlo nunca.
   */
  const filtrosPlegadosActivos = useMemo(
    () => CAMPOS_PLEGADOS.filter((c) => filtros[c] !== FILTROS_INICIALES[c]).length,
    [filtros],
  );

  const abrirNuevo = () => { setEditando(null); setFormAbierto(true); };
  const abrirEdicion = (g: Gasto) => { setEditando(g); setFormAbierto(true); };

  /** Envuelve una acción de fila: bloquea sus botones y refresca al terminar. */
  const accion = async (gasto: Gasto, fn: () => Promise<unknown>) => {
    setOcupado(gasto.id);
    setErrorAccion(null);
    try {
      await fn();
      refresh();
    } catch (err: any) {
      setErrorAccion(err?.message || 'La operación ha fallado');
    } finally {
      setOcupado(null);
    }
  };

  const borrar = (g: Gasto) => {
    if (!confirm(
      `¿Eliminar este gasto?\n\n${g.propertyName}\n${g.concept} — ${formatImporte(g.amount)}\n\n` +
      'Desaparece del CRM y de la app de lavandería.',
    )) return;
    return accion(g, () => api.borrarGasto(g.id, g.propertyId));
  };

  const descobrar = (g: Gasto) => {
    if (!confirm(
      `¿Devolver este gasto a pendiente?\n\n${g.concept}\n\n` +
      'Se borra el importe cobrado. Si ya entró en una liquidación emitida, ' +
      'volverá a ofrecerse en la siguiente.',
    )) return;
    return accion(g, () => api.descobrarGasto(g.id, g.propertyId));
  };

  const exportarCsv = () => {
    const filas: (string | number)[][] = [
      ['Fecha', 'Propiedad', 'Concepto', 'Observaciones', 'Categoría', 'Estado', 'Importe (€)', 'Importe cobrado (€)'],
      ...gastos.map((g) => [
        g.expenseDate,
        g.propertyName,
        g.concept,
        g.observations || '',
        g.category || '',
        ETIQUETAS_ESTADO[g.status] || g.status,
        g.amount.toFixed(2),
        g.chargedAmount.toFixed(2),
      ]),
    ];
    const blob = new Blob([toCsv(filas)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = `gastos-mantenimiento-${new Date().toISOString().slice(0, 10)}.csv`;
    enlace.click();
    URL.revokeObjectURL(url);
  };

  const badgeEstado = (estado: string) => (
    <span className={classNames(
      'badge',
      estado === 'COBRADO' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-800',
    )}>
      {ETIQUETAS_ESTADO[estado] || estado}
    </span>
  );

  /**
   * Los botones de fila, compartidos por la tabla y las tarjetas de móvil.
   *
   * 44 px de lado en el móvil —el mínimo con el que un dedo acierta— y se
   * encogen a 36 en escritorio, donde apunta un ratón y una fila de tabla no
   * puede permitirse ese alto. Con `p-1.5` a secas quedaban en 26.
   */
  const acciones = (g: Gasto) => {
    const boton = 'h-11 w-11 sm:h-9 sm:w-9 inline-flex items-center justify-center rounded-lg transition-colors';
    return ocupado === g.id ? (
      <span className={boton}><Loader2 size={16} className="animate-spin text-sereno-dark/40" /></span>
    ) : (
      <>
        {g.status === 'PENDIENTE' ? (
          <button onClick={() => setCobrando(g)} aria-label="Marcar como cobrado" title="Marcar como cobrado" className={classNames(boton, 'hover:bg-green-50')}>
            <CheckCircle2 size={17} className="text-green-600" />
          </button>
        ) : (
          <button onClick={() => descobrar(g)} aria-label="Devolver a pendiente" title="Devolver a pendiente" className={classNames(boton, 'hover:bg-gray-100')}>
            <RotateCcw size={17} className="text-amber-600" />
          </button>
        )}
        <button onClick={() => abrirEdicion(g)} aria-label="Editar" title="Editar" className={classNames(boton, 'hover:bg-gray-100')}>
          <Pencil size={17} className="text-sereno-dark/60" />
        </button>
        <button onClick={() => borrar(g)} aria-label="Eliminar" title="Eliminar" className={classNames(boton, 'hover:bg-red-50')}>
          <Trash2 size={17} className="text-red-500" />
        </button>
      </>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-sereno-blue/50 text-sereno-dark">
            <Wrench size={22} />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Gastos</h1>
            {/* En móvil se cae: al lado de los dos botones le quedan unos 130 px
                y se parte en seis líneas que empujan la tabla fuera de la vista. */}
            <p className="hidden sm:block text-sm text-sereno-dark/50">
              Gastos de mantenimiento por propiedad, los mismos que la app de lavandería
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={exportarCsv}
            disabled={gastos.length === 0}
            className="btn-ghost min-h-11 flex items-center gap-2 text-sm disabled:opacity-40"
            aria-label="Exportar a CSV"
            title="Exportar a CSV"
          >
            <Download size={16} /> <span className="hidden sm:inline">CSV</span>
          </button>
          <button onClick={abrirNuevo} className="btn-primary min-h-11 flex items-center gap-2 text-sm">
            <Plus size={18} />
            <span className="hidden sm:inline">Nuevo gasto</span><span className="sm:hidden">Nuevo</span>
          </button>
        </div>
      </div>

      {/* El estado se queda fuera del plegado: en el móvil la pregunta que se
          viene a hacer aquí es «qué me queda por cobrar», así que es un
          segmentado a un toque y no una opción escondida. El resto se despliega. */}
      <PanelFiltros
        activos={filtrosPlegadosActivos}
        barra={
          <div
            role="group"
            aria-label="Estado"
            className="flex w-full sm:w-auto rounded-lg border border-sereno-dark/15 p-0.5 gap-0.5"
          >
            {ESTADOS_BARRA.map(({ valor, etiqueta }) => (
              <button
                key={valor}
                onClick={() => cambiar('estado', valor)}
                aria-pressed={filtros.estado === valor}
                className={classNames(
                  'flex-1 sm:flex-none min-h-11 px-3 rounded-md text-sm font-medium transition-colors whitespace-nowrap',
                  filtros.estado === valor
                    ? 'bg-sereno-dark text-white dark:bg-gray-100 dark:text-gray-900'
                    : 'text-sereno-dark/60 hover:bg-sereno-blue/50',
                )}
              >
                {etiqueta}
              </button>
            ))}
          </div>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="sm:col-span-2 lg:col-span-1">
            <label className="label" htmlFor="filtro-propiedad">Propiedad</label>
            <select
              id="filtro-propiedad"
              className="input min-h-11"
              value={filtros.propertyId}
              onChange={(e) => cambiar('propertyId', e.target.value)}
            >
              <option value="all">Todas</option>
              {(propiedades || []).map((p) => (
                <option key={p.id} value={p.id}>{p.nickname}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="filtro-mes">Mes</label>
            <select id="filtro-mes" className="input min-h-11" value={filtros.mes} onChange={(e) => cambiar('mes', e.target.value)}>
              <option value="">Todos</option>
              {MESES.map((m, i) => (
                <option key={m} value={String(i + 1)}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="filtro-anio">Año</label>
            <select id="filtro-anio" className="input min-h-11" value={filtros.anio} onChange={(e) => cambiar('anio', e.target.value)}>
              <option value="">Todos</option>
              {ANIOS.map((a) => (
                <option key={a} value={String(a)}>{a}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="filtro-categoria">Categoría</label>
            <select id="filtro-categoria" className="input min-h-11" value={filtros.categoria} onChange={(e) => cambiar('categoria', e.target.value)}>
              <option value="all">Todas</option>
              {CATEGORIAS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        {JSON.stringify(filtros) !== JSON.stringify(FILTROS_INICIALES) && (
          <button
            onClick={() => setFiltros(FILTROS_INICIALES)}
            className="mt-3 min-h-11 text-sm text-sereno-dark/50 hover:text-sereno-dark underline"
          >
            Limpiar filtros
          </button>
        )}
      </PanelFiltros>

      {errorAccion && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          {errorAccion}
        </div>
      )}

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <EmptyState title="No se han podido cargar los gastos" description={error} />
      ) : gastos.length === 0 ? (
        <EmptyState
          title="No hay gastos con estos filtros"
          description="Prueba a ampliar el periodo o crea el primero."
          action={<button onClick={abrirNuevo} className="btn-primary"><Plus size={16} className="inline mr-1" /> Nuevo gasto</button>}
        />
      ) : (
        <>
          {/* Tres cifras en rejilla y no una frase corrida: en el móvil la frase
              se partía en tres renglones que había que leer, y esto se mira. */}
          <div className="card mb-4 py-3">
            {/* Cuatro casillas y no tres: metiendo «· 14 pend.» dentro de la
                primera, su etiqueta partía en dos líneas y dejaba la fila
                desigual. Con una palabra cada una caben 2×2 en el móvil. */}
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center sm:text-left">
              <div>
                <dd className="text-lg font-bold text-sereno-dark tabular-nums">{gastos.length}</dd>
                <dt className="text-xs text-sereno-dark/50">gasto{gastos.length !== 1 ? 's' : ''}</dt>
              </div>
              <div>
                <dd className="text-lg font-bold text-sereno-dark tabular-nums">{totales.pendientes}</dd>
                <dt className="text-xs text-sereno-dark/50">pendiente{totales.pendientes !== 1 ? 's' : ''}</dt>
              </div>
              <div>
                <dd className="text-lg font-bold text-sereno-dark tabular-nums">{formatImporte(totales.importe)}</dd>
                <dt className="text-xs text-sereno-dark/50">importe</dt>
              </div>
              <div>
                <dd className="text-lg font-bold text-green-600 tabular-nums">{formatImporte(totales.cobrado)}</dd>
                <dt className="text-xs text-sereno-dark/50">cobrado</dt>
              </div>
            </dl>
            {/* El backend recorta a 1.000 líneas; si hay más, mejor decirlo que
                dejar creer que el total de arriba es todo lo que existe. */}
            {data && data.total > gastos.length && (
              <p className="mt-2 pt-2 border-t border-gray-100 text-xs text-amber-600">
                Mostrando {gastos.length} de {data.total}: afina los filtros.
              </p>
            )}
          </div>

          {/* Tabla en escritorio */}
          <div className="card overflow-x-auto hidden md:block p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-sereno-dark/50 border-b border-gray-100">
                  <th className="px-4 py-3 font-medium">Fecha</th>
                  <th className="px-4 py-3 font-medium">Propiedad</th>
                  <th className="px-4 py-3 font-medium">Concepto</th>
                  <th className="px-4 py-3 font-medium">Categoría</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium text-right">Importe</th>
                  <th className="px-4 py-3 font-medium text-right">Cobrado</th>
                  <th className="px-4 py-3 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {gastos.map((g) => (
                  <tr key={g.id} className="border-b border-gray-50 last:border-0 hover:bg-sereno-blue/10">
                    <td className="px-4 py-3 whitespace-nowrap text-sereno-dark/70">{formatDate(g.expenseDate)}</td>
                    <td className="px-4 py-3 font-medium text-sereno-dark">{g.propertyName}</td>
                    <td className="px-4 py-3 max-w-xs">
                      <div className="truncate text-sereno-dark" title={g.concept}>{g.concept}</div>
                      {g.observations && (
                        <div className="truncate text-xs text-sereno-dark/40" title={g.observations}>{g.observations}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sereno-dark/60">{g.category || '—'}</td>
                    <td className="px-4 py-3">{badgeEstado(g.status)}</td>
                    <td className="px-4 py-3 text-right font-medium whitespace-nowrap">{formatImporte(g.amount)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {g.chargedAmount > 0
                        ? <span className="font-semibold text-green-600">{formatImporte(g.chargedAmount)}</span>
                        : <span className="text-sereno-dark/30">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">{acciones(g)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Tarjetas en móvil */}
          <div className="space-y-3 md:hidden">
            {gastos.map((g) => (
              <div key={g.id} className="card">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-sereno-dark truncate">{g.propertyName}</div>
                    <div className="text-xs text-sereno-dark/50">{formatDate(g.expenseDate)}</div>
                  </div>
                  {badgeEstado(g.status)}
                </div>
                <div className="mt-2 text-sm text-sereno-dark">{g.concept}</div>
                {g.observations && <div className="text-xs text-sereno-dark/40">{g.observations}</div>}
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-sereno-dark/60">
                  <span>Importe: <strong className="text-sereno-dark">{formatImporte(g.amount)}</strong></span>
                  {g.chargedAmount > 0 && <span>Cobrado: <strong className="text-green-600">{formatImporte(g.chargedAmount)}</strong></span>}
                  {g.category && <span>{g.category}</span>}
                </div>
                <div className="mt-3 flex items-center gap-1 border-t border-gray-100 pt-2">{acciones(g)}</div>
              </div>
            ))}
          </div>
        </>
      )}

      <ModalGasto
        abierto={formAbierto}
        gasto={editando}
        propiedades={propiedades || []}
        onCerrar={() => { setFormAbierto(false); setEditando(null); }}
        onGuardado={refresh}
      />

      <ModalCobrar
        abierto={Boolean(cobrando)}
        gasto={cobrando}
        onCerrar={() => setCobrando(null)}
        onCobrado={refresh}
      />
    </div>
  );
}
